import React, { useState } from 'react';
import { CheckIcon, ChevronDownIcon, InfinityIcon, GiftIcon } from 'lucide-react';
import { QuantityStepper, money } from './QuantityStepper';
import { GoldMedal } from './LifetimeSeal';
import { PrescriptionBadge } from './PrescriptionBadge';
import { offers, Offer, checkoutUrl } from '../data/product';
import { Link } from 'react-router-dom';
import { CompanyPack } from './CompanyPack';
import { IncludedGear } from './IncludedGear';
import { UpsellModal } from './UpsellModal';
import { useUpsellFlow } from '../hooks/useUpsellFlow';
import { flowForSingle, isLifetimeToggleAvailable, LIFETIME_UPGRADE_PRICE, SINGLE_WITH_LIFETIME_PRICE, UPSELL_VARIANTS } from '../data/upsellFlows';
import { PaymentLogos } from './PaymentLogos';
import { UspsBadge } from './UspsBadge';
import { SecureCheckoutNote } from './SecureCheckoutNote';
import { trackCustom, packEvents, trackShopifyAddToCart } from '../utils/tracking';
import { LaunchOfferPanel } from './LaunchOfferPanel';
import { PromoCountdown } from './PromoCountdown';
import { PRODUCT_NAME, warrantyCopy, pricing } from '../data/promotion';
import { useActiveSale } from '../hooks/useActiveSale';
interface OfferSectionProps {
  selectedOffer: Offer['id'];
  onSelectOffer: (id: Offer['id']) => void;
}
const packMath: Record<Offer['id'], {
  price: number;
  units: number;
  compareAt?: number;
  save?: number;
}> = {
  single: {
    price: 49.95,
    units: 1,
    compareAt: 124.95,
    save: 75
  },
  trio: {
    price: 99.9,
    units: 3,
    compareAt: 374.85,
    save: 274.95
  },
  crew: {
    price: 179.95,
    units: 6,
    compareAt: 749.7,
    save: 569.75
  }
};
export function OfferSection({
  selectedOffer,
  onSelectOffer
}: OfferSectionProps) {
  const [quantities, setQuantities] = useState<Record<Offer['id'], number>>({
    single: 1,
    trio: 1,
    crew: 1
  });
  // Optional +$19.95 lifetime upgrade on a single system. Never preselected.
  const [singleLifetime, setSingleLifetime] = useState(false);
  const upsell = useUpsellFlow();
  const sale = useActiveSale();
  const lifetimeToggle = isLifetimeToggleAvailable();
  const handleQuantity = (id: Offer['id'], next: number) => {
    // The lifetime add-on is a single-unit option; more units run the free-third flow instead.
    if (id === 'single' && next !== 1) setSingleLifetime(false);
    setQuantities(prev => ({
      ...prev,
      [id]: next
    }));
  };
  return <section id="offer" className="border-b border-white/10 bg-ink-900" data-id="element-535" data-mp="3ot3e32jz7c52">
      <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20" data-id="element-536" data-mp="3b2goq103uyln">
        <h2 className="grit-headline text-[clamp(2.5rem,9vw,5rem)] font-extrabold uppercase text-offwhite" data-id="element-537" data-mp="3fuhxve1z897g">
          Choose your <span className="text-grit-500" data-id="element-538" data-mp="316n3lt8blws9">loadout.</span>
        </h2>
        <p className="mt-4 text-lg font-medium text-steel-300 sm:text-xl" data-id="element-539" data-mp="363vqu88bsr0d">
          {PRODUCT_NAME}. Every X1 ships as the complete system.
        </p>

        <LaunchOfferPanel data-id="element-540" data-mp="2s5fgm7b98idh" />

        <div className="mt-6 border-2 gold-edge bg-[#1a1408] p-5 sm:p-6" data-id="element-541" data-mp="2x3qqgi1jusvp">
          <p className="flex flex-wrap items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.24em] text-[#f2c94c]" data-id="element-542" data-mp="2x5tfozuvohzd">
            <InfinityIcon className="h-4 w-4" aria-hidden="true" data-id="element-543" data-mp="28j3x9d6o5qpb" />
            {warrantyCopy.value} · Free with every multi-pack
          </p>
          <p className="grit-headline mt-2 text-[clamp(1.75rem,5vw,3rem)] font-extrabold uppercase gold-text" data-id="element-544" data-mp="2mjes0rb3npfr">
            {warrantyCopy.title}
          </p>
          <p className="mt-2 max-w-3xl text-[15px] font-semibold text-steel-200 sm:text-lg" data-id="element-545" data-mp="2wgjaag1tr0ra">
            {warrantyCopy.tagline} Break it, crack it, scratch a lens and we send a replacement. You never buy work
            glasses or a headlamp again. Included on Buy 2 Get 1 Free, Buy 4 Get 2 Free and the Crew Pack; single
            systems carry 1-year coverage.{' '}
            <Link to="/legal/guarantee" className="text-[13px] font-medium text-steel-400 underline" data-id="element-546" data-mp="319vodw8vnp5s">
              Terms apply.
            </Link>
          </p>
        </div>

        <ul className="mt-10 flex flex-col gap-6 lg:grid lg:grid-cols-3 lg:items-start" data-id="element-547" data-mp="3a9skgixljqav">
          {offers.map(offer => {
          const isSelected = selectedOffer === offer.id;
          const qty = quantities[offer.id];
          const math = packMath[offer.id];
          const withLifetime = offer.id === 'single' && singleLifetime && qty === 1;
          const orderTotal = withLifetime ? SINGLE_WITH_LIFETIME_PRICE : math.price * qty;
          const orderUnits = math.units * qty;
          return <li key={offer.id} id={`pack-${offer.id}`} className={`h-full scroll-mt-28 ${offer.featured ? '-order-1 lg:order-none' : ''}`} data-id="element-548" data-mp="pmiqnrwws7n2">
                <article className={`relative flex h-full flex-col border bg-ink-950 p-6 transition-colors duration-150 ease-grit lg:p-7 ${isSelected ? 'border-grit-500' : 'border-white/12 hover:border-white/30'} ${offer.featured ? 'lg:-mt-4 lg:pb-9' : ''}`} data-id="element-549" data-mp="yn72qh2qrxxb">
                  <p className="-mx-6 -mt-6 mb-5 flex items-center justify-between gap-3 bg-grit-500 px-6 py-2.5 text-ink-950 lg:-mx-7 lg:-mt-7 lg:px-7" data-id="element-550" data-mp="2p3arzjv8tlzp">
                    <span className="font-display text-2xl font-extrabold uppercase leading-none" data-id="element-551" data-mp="2k6aj7170e54w">60% off</span>
                    <span className="text-right text-[10px] font-extrabold uppercase leading-tight tracking-[0.16em]" data-id="element-552" data-mp="2bbmdj9xiulfk">
                      {sale.name}
                      {math.units > 1 && <span className="block" data-id="element-553" data-mp="26dul8esysxau">+ free units</span>}
                    </span>
                  </p>

                  {offer.image ? <>
                      {/* The pack artwork already carries the badge, the offer, the unit count and the per-X1 price. */}
                      <h3 className="sr-only" data-id="element-554" data-mp="1sel6zkobkd9i">
                        {[offer.eyebrow, offer.kicker, offer.name].filter(Boolean).join(' · ')}
                      </h3>
                      <img src={offer.image} alt={offer.imageAlt ?? offer.name} loading="lazy" width={1122} height={1402} className="h-auto w-full border border-white/10" data-id="element-555" data-mp="3mruifi0fe8ue" />
                    </> : <>
                      {offer.eyebrow && <p className={`mb-3 inline-flex self-start px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] ${offer.featured ? 'bg-grit-500 text-ink-950' : 'border border-grit-500/60 text-grit-500'}`} data-id="element-556" data-mp="22um3x90hd8bb">
                          {offer.eyebrow}
                        </p>}
                      {offer.kicker && <p className="text-xs font-bold uppercase tracking-[0.22em] text-grit-500" data-id="element-557" data-mp="id7gynfa4214">{offer.kicker}</p>}
                      <h3 className={`mt-1 font-display font-extrabold uppercase leading-none text-offwhite ${offer.featured ? 'text-4xl sm:text-5xl' : 'text-3xl sm:text-4xl'}`} data-id="element-558" data-mp="ewuuuy79cnmc">
                        {offer.name}
                      </h3>
                      <p className="mt-2 text-xs font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-559" data-mp="jx92wg8uzxkd">{offer.units}</p>
                    </>}

                  <div className="mt-6 flex flex-wrap items-end gap-x-3 gap-y-1" data-id="element-560" data-mp="3ncy252ugkaa5">
                    <span className={`font-display font-extrabold leading-none text-offwhite ${offer.featured ? 'text-6xl sm:text-7xl' : 'text-5xl'}`} data-id="element-561" data-mp="3natpnwihu2y9">
                      {money(orderTotal)}
                    </span>
                    {math.compareAt && !withLifetime && <span className="pb-1 text-base text-steel-500 line-through" data-id="element-562" data-mp="34lwr3gg2coa8">
                        {money(math.compareAt * qty)}
                      </span>}
                  </div>

                  {withLifetime ? <p className="mt-2 font-display text-xl font-bold uppercase tracking-wide text-[#ffe79a]" data-id="element-563" data-mp="2t0i4rsq4fq7u">
                      {money(math.price)} + {money(LIFETIME_UPGRADE_PRICE)} lifetime
                    </p> : math.save && <p className="mt-2 font-display text-xl font-bold uppercase tracking-wide text-grit-500" data-id="element-564" data-mp="370sugsfp2yrf">
                        You save {money(math.save * qty)}
                      </p>}
                  {offer.perUnit && !offer.image && <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-steel-300" data-id="element-565" data-mp="3gsgggdthfnli">
                      {offer.perUnit}
                    </p>}
                  {qty > 1 && <p className="mt-1 text-sm font-semibold uppercase tracking-wide text-steel-300" data-id="element-566" data-mp="1e3ozq07c7d1g">
                      {qty} packs · {orderUnits} X1 total
                    </p>}

                  {offer.highlight && !offer.image && <p className="mt-4 bg-grit-500 px-4 py-3 font-display text-2xl font-extrabold uppercase leading-none tracking-wide text-ink-950 sm:text-3xl" data-id="element-567" data-mp="153io6jv78n4x">
                      {offer.highlight}
                    </p>}
                  {offer.runtime && <p className="mt-3 font-display text-lg font-bold uppercase leading-tight tracking-wide text-offwhite" data-id="element-568" data-mp="1std1f2jfkdpp">
                      {offer.runtime}
                    </p>}

                  <p className="mt-4 text-[13px] font-semibold uppercase tracking-wide text-steel-300" data-id="element-569" data-mp="1j2rsal64rqsj">
                    {money(pricing.separateValue)} total value per X1
                  </p>

                  <div className="mt-5 border-t border-white/10 pt-5" data-id="element-570" data-mp="2bq4ciootjmio">
                    {/* Everyday items are pictured in the artwork's icon strip, so the list lives one tap away. */}
                    <details className="group" data-id="element-571" data-mp="3qj15h0kib7dx">
                      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-[11px] font-bold uppercase tracking-[0.2em] text-steel-300 transition-colors duration-150 ease-grit hover:text-offwhite [&::-webkit-details-marker]:hidden" data-id="element-572" data-mp="99m9t1a3mcmx">
                        Full list of what&apos;s in the box
                        <ChevronDownIcon className="h-4 w-4 shrink-0 transition-transform duration-150 ease-grit group-open:rotate-180" aria-hidden="true" data-id="element-573" data-mp="dbzedmd404i4" />
                      </summary>
                      <ul className="mt-3 grid gap-2 text-[14px] font-medium text-steel-200" data-id="element-574" data-mp="110s6t8uivu7z">
                        {offer.includes.filter(line => !/1-year|warranty|mystery gift/i.test(line)).map(line => <li key={line} className="flex items-start gap-2.5" data-id="element-575" data-mp="1foab4646amtv">
                              <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-576" data-mp="1klah6h10vpkz" />
                              {line}
                            </li>)}
                      </ul>
                    </details>
                    <ul className="mt-4 grid gap-2 text-[15px] font-medium text-offwhite" data-id="element-577" data-mp="wwhkujghnfpu">
                      {offer.includes.filter(line => /1-year|warranty|mystery gift/i.test(line)).map(line => {
                    if (/1-year/i.test(line)) {
                      return <li key={line} className="mt-1 grid gap-2" data-id="element-578" data-mp="rxeoroihkju0">
                              <span className="flex items-start gap-2.5" data-id="element-579" data-mp="rvk1i3jqsfmr">
                                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-580" data-mp="dyx5c2zm5thw" />
                                {line}
                              </span>
                              {lifetimeToggle && qty === 1 && <label className={`flex cursor-pointer items-start gap-3 border p-3.5 transition-colors duration-150 ease-grit ${singleLifetime ? 'gold-edge bg-[#1a1408]' : 'border-white/15 hover:border-white/30'}`} data-id="element-581" data-mp="2fv5t5m0rkgcn">
                                  <input type="checkbox" checked={singleLifetime} onChange={event => setSingleLifetime(event.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-[#f2c94c]" data-id="element-582" data-mp="2zjoka95by3tz" />
                                  <span data-id="element-583" data-mp="1j6pwl5z8l6q1">
                                    <span className="block font-display text-base font-bold uppercase leading-tight text-[#ffe79a]" data-id="element-584" data-mp="1e5sjh7t332ve">
                                      Add Lifetime Replacement Warranty +{money(LIFETIME_UPGRADE_PRICE)}
                                    </span>
                                    <span className="mt-0.5 block text-[13px] font-semibold text-steel-300" data-id="element-585" data-mp="1e0kw12utmsbu">
                                      Break it. We replace it. Order total {money(SINGLE_WITH_LIFETIME_PRICE)}.
                                    </span>
                                  </span>
                                </label>}
                              <button type="button" onClick={() => {
                          onSelectOffer('trio');
                          document.getElementById('pack-trio-cta')?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'end'
                          });
                        }} className="group flex items-center gap-3 border gold-edge bg-[#1a1408] p-3.5 text-left transition-colors duration-150 ease-grit hover:bg-[#241b0b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f2c94c]" data-id="element-586" data-mp="1rq4uvxla75fv">
                                <InfinityIcon className="h-7 w-7 shrink-0 text-[#f2c94c]" aria-hidden="true" data-id="element-587" data-mp="26rtph9nzks9u" />
                                <span data-id="element-588" data-mp="1e58pjktg37uo">
                                  <span className="block font-display text-base font-bold uppercase leading-tight text-[#ffe79a]" data-id="element-589" data-mp="1e6af0ub7jwvx">
                                    Want it covered for life?
                                  </span>
                                  <span className="mt-0.5 block text-[13px] font-semibold text-steel-300" data-id="element-590" data-mp="1nwd5onuuwyr3">
                                    For just $49.95 more: 2 extra X1 plus the Lifetime Replacement Warranty on all
                                    3. Upgrade to Buy 2, Get 1 Free →
                                  </span>
                                </span>
                              </button>
                            </li>;
                    }
                    if (/warranty/i.test(line)) {
                      return <li key={line} className="mt-1" data-id="element-591" data-mp="2t3whgx729n7s">
                              <Link to="/legal/guarantee" className="group flex items-center gap-4 border gold-edge bg-[#1a1408] p-4 transition-[background-color,box-shadow] duration-150 ease-grit hover:bg-[#241b0b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#f2c94c]" data-id="element-592" data-mp="2y0wpverdv222">
                                <GoldMedal size={86} data-id="element-593" data-mp="3grxo20koitkk" />
                                <p className="font-display text-lg font-bold uppercase leading-tight tracking-wide text-[#ffe79a]" data-id="element-594" data-mp="3gqcrk5et10k2">
                                  {math.units > 1 ? `All ${math.units} covered for life.` : 'Covered for life.'}
                                  <span className="mt-0.5 block text-[13px] font-semibold normal-case tracking-normal text-steel-300" data-id="element-595" data-mp="1laeg3cdh3b2y">
                                    {warrantyCopy.title}. {warrantyCopy.tagline} {warrantyCopy.value},{' '}
                                    {warrantyCopy.status.toLowerCase()}.
                                  </span>
                                </p>
                              </Link>
                            </li>;
                    }
                    if (/mystery gift/i.test(line)) {
                      return <li key={line} className="mt-1 border border-grit-500/60 bg-grit-500/10 p-3.5 font-display text-lg font-bold uppercase leading-tight tracking-wide text-grit-500" data-id="element-596" data-mp="tiyiyoeai5yh">
                              <span className="flex items-center gap-2" data-id="element-597" data-mp="2iyeqy0gh9lms">
                                <GiftIcon className="h-4 w-4" aria-hidden="true" data-id="element-598" data-mp="2iz69a9l2dmv4" />
                                Free mystery gift
                              </span>
                              <span className="mt-1 block text-[13px] font-semibold normal-case tracking-normal text-steel-300" data-id="element-599" data-mp="2vfz0tdte2ihe">
                                There&apos;s something else in the box. No clues. No extra charge. While supplies
                                last.
                              </span>
                            </li>;
                    }
                    return <li key={line} className="flex items-start gap-2.5" data-id="element-600" data-mp="2ck8vphq5t5za">
                            <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-grit-500" aria-hidden="true" data-id="element-601" data-mp="2hfo8e6cfkvu4" />
                            {line}
                          </li>;
                  })}
                    </ul>
                    {offer.splitNote && <div className="mt-4 border-l-2 border-grit-500 bg-white/[0.03] px-4 py-3" data-id="element-602" data-mp="rajy12fdzao">
                        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-grit-500" data-id="element-603" data-mp="muxe5ih0tpw4">
                          {offer.splitNote.title}
                        </p>
                        <p className="mt-1 text-[14px] font-medium text-steel-200" data-id="element-604" data-mp="wk78l4zc7kne">{offer.splitNote.body}</p>
                        <p className="mt-1 font-display text-base font-bold uppercase tracking-wide text-offwhite" data-id="element-605" data-mp="rs789bfcupqa">
                          {offer.splitNote.each}
                        </p>
                      </div>}
                    <PrescriptionBadge className="mt-3" compact data-id="element-606" data-mp="10nwrpex8ta11" />
                    <ul className="mt-4 grid gap-1 border-t border-white/5 pt-4 text-sm text-steel-400" data-id="element-607" data-mp="1fhi6om98g819">
                      {offer.supporting.map(line => <li key={line} data-id="element-608" data-mp="3h9o4p9h8l6wz">{line}</li>)}
                    </ul>
                  </div>

                  <div className="mt-auto pt-7" data-id="element-609" data-mp="3cc4r8bsd4y63">
                    <QuantityStepper value={qty} onChange={next => handleQuantity(offer.id, next)} label={`${offer.name} quantity`} className="mb-4" data-id="element-610" data-mp="3cdfwkg1gidzb" />
                    {(() => {
                  const ctaClass = `flex w-full scroll-mb-28 items-center justify-center gap-2 px-5 py-4 text-center font-display font-extrabold uppercase tracking-wide transition-[background-color,color,transform] duration-150 ease-grit active:translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-grit-500 ${offer.featured ? 'bg-grit-500 text-ink-950 hover:bg-grit-400 text-2xl' : 'border-2 border-grit-500 text-grit-500 hover:bg-grit-500 hover:text-ink-950 text-xl'}`;
                  const ctaLabel = withLifetime ? `Get the X1 + Lifetime — ${money(orderTotal)}` : qty > 1 ? `Get ${orderUnits} X1 for ${money(orderTotal)}` : offer.cta;
                  const cartVariant = withLifetime ? UPSELL_VARIANTS.SINGLE_X1_LIFETIME_6990_VARIANT_ID : offer.variantId as string;
                  const cartQty = withLifetime ? 1 : qty;
                  const track = () => {
                    onSelectOffer(offer.id);
                    trackCustom(packEvents[offer.id].addToCart, {
                      quantity: qty,
                      value: orderTotal
                    });
                  };
                  return offer.variantId ? <a id={`pack-${offer.id}-cta`} href={checkoutUrl(cartVariant, cartQty)} data-buy-intent="true" onClick={event => {
                    track();
                    // Single systems get exactly one negotiation flow before the cart.
                    const flow = offer.id === 'single' ? flowForSingle(qty, withLifetime) : null;
                    if (flow) {
                      event.preventDefault();
                      upsell.start(flow);
                      return;
                    }
                    trackShopifyAddToCart(event, {
                      content_name: withLifetime ? 'GritVue X1 Complete System + Lifetime Replacement Warranty' : offer.name,
                      content_ids: [cartVariant],
                      quantity: cartQty,
                      value: Number(orderTotal.toFixed(2))
                    });
                  }} className={ctaClass} data-id="element-611" data-mp="fo6xi1ifjljj">
                          {ctaLabel}
                        </a> : <button id={`pack-${offer.id}-cta`} type="button" onClick={track} className={ctaClass} data-id="element-612" data-mp="1qdsghvdtrk57">
                          {ctaLabel}
                        </button>;
                })()}
                    <PromoCountdown variant="card" className="mt-3" data-id="element-613" data-mp="3vdbfixk8lwbg" />
                    <UspsBadge className="mt-3" note={offer.note} data-id="element-614" data-mp="41ietu366lld" />
                    <SecureCheckoutNote className="mt-4" data-id="element-615" data-mp="82twndfsy2j0" />
                    {offer.badge && <p className="mt-2 text-xs font-bold uppercase tracking-[0.16em] text-grit-500" data-id="element-616" data-mp="37ih5in14988t">
                        {offer.badge}
                      </p>}
                    {offer.footnote && <p className="mt-3 text-[11px] leading-relaxed text-steel-500" data-id="element-617" data-mp="3ggu7af7cgxt4">{offer.footnote}</p>}
                  </div>
                </article>
              </li>;
        })}
        </ul>

        <IncludedGear data-id="element-618" data-mp="vuix5h2s1xb1" />

        <CompanyPack data-id="element-619" data-mp="10qq0r37pawpt" />

        <UpsellModal step={upsell.step} routing={upsell.routing} onAccept={upsell.accept} onDecline={upsell.decline} onClose={upsell.close} data-id="element-620" data-mp="10q7tlbji2y34" />

        <div className="mt-10 flex flex-col gap-4 border-t border-white/10 pt-8 sm:flex-row sm:items-center sm:justify-between" data-id="element-621" data-mp="15xede6f355a4">
          <div data-id="element-622" data-mp="113a6fn5tkx91">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-623" data-mp="x3rl4qa36k5o">Secure checkout</p>
            <div className="mt-2" data-id="element-624" data-mp="s4ezu9yv5l57">
              <PaymentLogos data-id="element-625" data-mp="s5q5ygfs74zh" />
            </div>
          </div>
          <p className="max-w-md text-[13px] text-steel-400" data-id="element-626" data-mp="n5vbk2wsrx9o">
            30-day returns with free return shipping · Lifetime warranty on multi-packs ·{' '}
            <Link to="/legal/shipping" className="font-semibold text-grit-500 hover:underline" data-id="element-627" data-mp="i6shtem9pdh0">
              Shipping &amp; returns
            </Link>
          </p>
        </div>
      </div>
    </section>;
}